import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  useColorMode,
  SimpleGrid,
  Icon,
  Flex,
  List,
  ListItem,
  ListIcon,
  Divider,
  Badge,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useToast,
  Spinner,
  Checkbox,
} from '@chakra-ui/react';
import { NavbarWithCallToAction } from '../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../../brandConfig';
import {
  FaCheckCircle,
  FaRocket,
  FaCode,
  FaGraduationCap,
  FaUsers,
  FaGlobe,
  FaBitcoin,
  FaLightbulb,
  FaPaperPlane,
  FaLinkedin,
  FaGithub,
} from 'react-icons/fa';
import { GiPokerHand } from 'react-icons/gi';
import { useMutation, gql } from '@apollo/client';

const SUBMIT_CAREER_APPLICATION = gql`
  mutation SubmitContactForm($input: SubmitContactFormInput!) {
    submitContactForm(input: $input) {
      id
      firstName
      lastName
      email
    }
  }
`;

export const Careers = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentRole: '',
    currentCompany: '',
    yearsExperience: '',
    blockchainExperience: '',
    linkedIn: '',
    github: '',
    portfolioUrl: '',
    interestedRole: '',
    coverLetter: '',
    heardAboutUs: '',
    availableToStart: '',
    willingToRelocate: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [submitApplication] = useMutation(SUBMIT_CAREER_APPLICATION);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitApplication({
        variables: {
          input: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            companyName: formData.currentCompany,
            industry: formData.currentRole,
            website: formData.portfolioUrl || formData.linkedIn || formData.github,
            numberOfEmployees: formData.yearsExperience,
            message: `
CAREER APPLICATION - ${formData.interestedRole || 'General Application'}

Current Role: ${formData.currentRole}
Current Company: ${formData.currentCompany}
Years of Experience: ${formData.yearsExperience}
Blockchain Experience: ${formData.blockchainExperience}

LinkedIn: ${formData.linkedIn || 'Not provided'}
GitHub: ${formData.github || 'Not provided'}
Portfolio: ${formData.portfolioUrl || 'Not provided'}

Available to Start: ${formData.availableToStart}
Willing to Relocate: ${formData.willingToRelocate ? 'Yes' : 'No'}
How They Heard About Us: ${formData.heardAboutUs || 'Not specified'}

--- COVER LETTER ---
${formData.coverLetter}
            `.trim(),
            formType: 'careers',
            submissionUrl: window.location.href,
            notificationEmails: [
              'tom@block52.xyz',
              'lucas@block52.xyz',
              'dynamict@protonmail.com'
            ],
          },
        },
      });

      setIsSubmitted(true);
      toast({
        title: 'Application Submitted!',
        description: "Thank you for your interest in Block52. We'll be in touch soon.",
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: 'Submission Error',
        description: 'There was an error submitting your application. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const careerBenefits = [
    {
      icon: FaRocket,
      title: 'Cutting-Edge Technology',
      description: 'Work on a purpose-built blockchain using Cosmos SDK, advanced cryptography, and innovative smart contract patterns.',
      color: '#3b82f6'
    },
    {
      icon: FaBitcoin,
      title: 'Web3 & Blockchain Expertise',
      description: 'Gain deep expertise in blockchain development, tokenomics, DeFi protocols, and decentralized systems.',
      color: '#64ffda'
    },
    {
      icon: FaCode,
      title: 'Full-Stack Development',
      description: 'Build everything from low-level cryptographic functions to beautiful React frontends and scalable backends.',
      color: '#3b82f6'
    },
    {
      icon: FaGraduationCap,
      title: 'Continuous Learning',
      description: 'Stay at the forefront of technology with regular learning sessions, conference attendance, and research time.',
      color: '#64ffda'
    },
    {
      icon: FaUsers,
      title: 'Collaborative Team',
      description: 'Work alongside experienced engineers, game theorists, and blockchain pioneers who are passionate about what they build.',
      color: '#3b82f6'
    },
    {
      icon: FaGlobe,
      title: 'Remote-First Culture',
      description: 'Work from anywhere in the world. We value results over hours and trust our team to manage their time.',
      color: '#64ffda'
    },
  ];

  const whyJoinReasons = [
    'Be part of building the first blockchain specifically designed for card games',
    'Work with cryptographic primitives like VRFs, commit-reveal schemes, and zero-knowledge proofs',
    'Contribute to open-source projects used by developers worldwide',
    'Help disrupt the $100B+ online gaming industry with provably fair technology',
    'Build a portfolio of work that demonstrates cutting-edge blockchain expertise',
    'Network with leaders in the blockchain and gaming industries',
    'Equity participation opportunities for key contributors',
    'Flexible working arrangements and competitive compensation',
  ];

  const rolesWeHire = [
    'Blockchain Engineers (Cosmos SDK, Rust, Go)',
    'Smart Contract Developers (Solidity, CosmWasm)',
    'Full-Stack Developers (TypeScript, React, Node.js)',
    'Cryptography Engineers',
    'DevOps & Infrastructure Engineers',
    'Technical Writers & Developer Advocates',
    'Product Managers',
    'UI/UX Designers',
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="1000px">
          <VStack spacing={10} align="start">
            {/* Header */}
            <VStack spacing={4} align="start" w="full">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                CAREERS
              </Badge>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Build the Future of Decentralized Gaming
              </Heading>
              <Text fontSize="xl" color={getColor('text.secondary', colorMode)} lineHeight="tall">
                Join Block52 and help create the infrastructure that will power the next generation of
                provably fair card games. We're looking for talented engineers who want to work on
                challenging problems at the intersection of blockchain, cryptography, and gaming.
              </Text>
            </VStack>

            <Divider />

            {/* Why Block52 on Your Resume */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                <Icon as={FaLightbulb} mr={3} color="#64ffda" />
                Why Block52 on Your Resume is a Career Boost
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={6}>
                Working at Block52 isn't just a job—it's an opportunity to gain expertise in one of the
                most innovative areas of technology. Here's why having Block52 on your resume will
                set you apart:
              </Text>

              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="#3b82f6"
                mb={6}
              >
                <Text color={getColor('text.secondary', colorMode)} fontWeight="medium" fontStyle="italic" fontSize="lg">
                  "Blockchain engineers with real-world experience in consensus mechanisms, cryptographic
                  protocols, and smart contract development are among the most sought-after professionals
                  in tech. At Block52, you'll build these skills while working on production systems."
                </Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {careerBenefits.map((benefit, index) => (
                  <Box
                    key={index}
                    p={6}
                    borderRadius="lg"
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-4px)',
                      borderColor: benefit.color,
                      boxShadow: `0 8px 16px ${benefit.color}30`,
                    }}
                  >
                    <Flex align="start" mb={3}>
                      <Flex
                        w="48px"
                        h="48px"
                        borderRadius="lg"
                        bg={`${benefit.color}20`}
                        align="center"
                        justify="center"
                        mr={4}
                        flexShrink={0}
                      >
                        <Icon as={benefit.icon} color={benefit.color} boxSize={6} />
                      </Flex>
                      <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        {benefit.title}
                      </Heading>
                    </Flex>
                    <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                      {benefit.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* What You'll Gain */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                <Icon as={GiPokerHand} mr={3} color="#3b82f6" />
                What You'll Gain
              </Heading>
              <List spacing={3}>
                {whyJoinReasons.map((reason, index) => (
                  <ListItem key={index}>
                    <Flex align="center">
                      <ListIcon as={FaCheckCircle} color="#64ffda" fontSize="lg" />
                      <Text color={getColor('text.secondary', colorMode)}>{reason}</Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Roles We Hire For */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Roles We Typically Hire For
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {rolesWeHire.map((role, index) => (
                  <Box
                    key={index}
                    p={4}
                    borderRadius="md"
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                  >
                    <Flex align="center">
                      <Icon as={FaCode} color="#64ffda" mr={3} />
                      <Text color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'} fontWeight="medium">
                        {role}
                      </Text>
                    </Flex>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Application Form */}
            <Box w="full" id="apply">
              <Heading as="h2" size="lg" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                <Icon as={FaPaperPlane} mr={3} color="#3b82f6" />
                Apply Now
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} mb={6}>
                Ready to join us? Fill out the form below and we'll be in touch if there's a match.
              </Text>

              {isSubmitted ? (
                <Box
                  p={8}
                  borderRadius="xl"
                  bg={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.1)' : 'green.50'}
                  border="2px solid"
                  borderColor="#64ffda"
                  textAlign="center"
                >
                  <Icon as={FaCheckCircle} color="#64ffda" boxSize={16} mb={4} />
                  <Heading size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Application Submitted!
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="lg">
                    Thank you for your interest in joining Block52. Our team will review your application
                    and get back to you within 5-7 business days if there's a potential fit.
                  </Text>
                </Box>
              ) : (
                <Box
                  as="form"
                  onSubmit={handleSubmit}
                  p={8}
                  borderRadius="xl"
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                  boxShadow="lg"
                >
                  <VStack spacing={6}>
                    {/* Personal Information */}
                    <Box w="full">
                      <Text fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'} mb={4}>
                        Personal Information
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl isRequired>
                          <FormLabel color={getColor('text.secondary', colorMode)}>First Name</FormLabel>
                          <Input
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="John"
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          />
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel color={getColor('text.secondary', colorMode)}>Last Name</FormLabel>
                          <Input
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Doe"
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          />
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel color={getColor('text.secondary', colorMode)}>Email</FormLabel>
                          <Input
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="john@example.com"
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel color={getColor('text.secondary', colorMode)}>Phone</FormLabel>
                          <Input
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+61 400 000 000"
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          />
                        </FormControl>
                      </SimpleGrid>
                    </Box>

                    <Divider />

                    {/* Professional Background */}
                    <Box w="full">
                      <Text fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'} mb={4}>
                        Professional Background
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl>
                          <FormLabel color={getColor('text.secondary', colorMode)}>Current Role / Title</FormLabel>
                          <Input
                            name="currentRole"
                            value={formData.currentRole}
                            onChange={handleInputChange}
                            placeholder="Senior Software Engineer"
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel color={getColor('text.secondary', colorMode)}>Current Company</FormLabel>
                          <Input
                            name="currentCompany"
                            value={formData.currentCompany}
                            onChange={handleInputChange}
                            placeholder="Acme Inc."
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          />
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel color={getColor('text.secondary', colorMode)}>Years of Experience</FormLabel>
                          <Select
                            name="yearsExperience"
                            value={formData.yearsExperience}
                            onChange={handleInputChange}
                            placeholder="Select..."
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          >
                            <option value="0-1">0-1 years (Entry Level)</option>
                            <option value="1-3">1-3 years</option>
                            <option value="3-5">3-5 years</option>
                            <option value="5-10">5-10 years</option>
                            <option value="10+">10+ years</option>
                          </Select>
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel color={getColor('text.secondary', colorMode)}>Blockchain Experience</FormLabel>
                          <Select
                            name="blockchainExperience"
                            value={formData.blockchainExperience}
                            onChange={handleInputChange}
                            placeholder="Select..."
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          >
                            <option value="none">No blockchain experience (eager to learn!)</option>
                            <option value="personal">Personal projects / learning</option>
                            <option value="1-2">1-2 years professional</option>
                            <option value="3+">3+ years professional</option>
                            <option value="expert">Expert / core contributor</option>
                          </Select>
                        </FormControl>
                      </SimpleGrid>
                    </Box>

                    <Divider />

                    {/* Links & Portfolio */}
                    <Box w="full">
                      <Text fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'} mb={4}>
                        Links & Portfolio
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <FormControl>
                          <FormLabel color={getColor('text.secondary', colorMode)}>
                            <Icon as={FaLinkedin} mr={2} />
                            LinkedIn URL
                          </FormLabel>
                          <Input
                            name="linkedIn"
                            value={formData.linkedIn}
                            onChange={handleInputChange}
                            placeholder="https://linkedin.com/in/..."
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel color={getColor('text.secondary', colorMode)}>
                            <Icon as={FaGithub} mr={2} />
                            GitHub URL
                          </FormLabel>
                          <Input
                            name="github"
                            value={formData.github}
                            onChange={handleInputChange}
                            placeholder="https://github.com/..."
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel color={getColor('text.secondary', colorMode)}>Portfolio / Website</FormLabel>
                          <Input
                            name="portfolioUrl"
                            value={formData.portfolioUrl}
                            onChange={handleInputChange}
                            placeholder="https://..."
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          />
                        </FormControl>
                      </SimpleGrid>
                    </Box>

                    <Divider />

                    {/* Role & Availability */}
                    <Box w="full">
                      <Text fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'} mb={4}>
                        Role & Availability
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl>
                          <FormLabel color={getColor('text.secondary', colorMode)}>Interested Role</FormLabel>
                          <Select
                            name="interestedRole"
                            value={formData.interestedRole}
                            onChange={handleInputChange}
                            placeholder="Select a role..."
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          >
                            <option value="blockchain-engineer">Blockchain Engineer</option>
                            <option value="smart-contract-dev">Smart Contract Developer</option>
                            <option value="fullstack-dev">Full-Stack Developer</option>
                            <option value="cryptography">Cryptography Engineer</option>
                            <option value="devops">DevOps / Infrastructure</option>
                            <option value="tech-writer">Technical Writer / DevRel</option>
                            <option value="product">Product Manager</option>
                            <option value="design">UI/UX Designer</option>
                            <option value="other">Other / General Application</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel color={getColor('text.secondary', colorMode)}>Available to Start</FormLabel>
                          <Select
                            name="availableToStart"
                            value={formData.availableToStart}
                            onChange={handleInputChange}
                            placeholder="Select..."
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          >
                            <option value="immediately">Immediately</option>
                            <option value="2-weeks">2 weeks notice</option>
                            <option value="1-month">1 month notice</option>
                            <option value="2-months">2 months notice</option>
                            <option value="3-months">3+ months</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel color={getColor('text.secondary', colorMode)}>How did you hear about us?</FormLabel>
                          <Select
                            name="heardAboutUs"
                            value={formData.heardAboutUs}
                            onChange={handleInputChange}
                            placeholder="Select..."
                            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                            _hover={{ borderColor: '#64ffda' }}
                            _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                          >
                            <option value="linkedin">LinkedIn</option>
                            <option value="twitter">Twitter/X</option>
                            <option value="discord">Discord</option>
                            <option value="github">GitHub</option>
                            <option value="referral">Referral</option>
                            <option value="search">Web Search</option>
                            <option value="conference">Conference / Event</option>
                            <option value="other">Other</option>
                          </Select>
                        </FormControl>
                        <FormControl display="flex" alignItems="center" pt={8}>
                          <Checkbox
                            name="willingToRelocate"
                            isChecked={formData.willingToRelocate}
                            onChange={handleCheckboxChange}
                            colorScheme="teal"
                          >
                            <Text color={getColor('text.secondary', colorMode)}>
                              Open to relocation if required
                            </Text>
                          </Checkbox>
                        </FormControl>
                      </SimpleGrid>
                    </Box>

                    <Divider />

                    {/* Cover Letter */}
                    <FormControl isRequired>
                      <FormLabel color={getColor('text.secondary', colorMode)}>
                        Why do you want to join Block52? Tell us about yourself.
                      </FormLabel>
                      <Textarea
                        name="coverLetter"
                        value={formData.coverLetter}
                        onChange={handleInputChange}
                        placeholder="Tell us about your background, what excites you about blockchain technology, why you want to join Block52, and what you could bring to the team..."
                        rows={8}
                        bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
                        borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.300'}
                        _hover={{ borderColor: '#64ffda' }}
                        _focus={{ borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }}
                      />
                    </FormControl>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      size="lg"
                      w="full"
                      py={7}
                      bgGradient="linear(to-r, #3b82f6, #64ffda)"
                      color="white"
                      _hover={{
                        bgGradient: 'linear(to-r, #64ffda, #3b82f6)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)',
                      }}
                      _active={{
                        transform: 'translateY(0)',
                      }}
                      borderRadius="lg"
                      fontWeight="bold"
                      fontSize="lg"
                      isLoading={isSubmitting}
                      loadingText="Submitting..."
                      leftIcon={isSubmitting ? <Spinner size="sm" /> : <FaPaperPlane />}
                    >
                      Submit Application
                    </Button>
                  </VStack>
                </Box>
              )}
            </Box>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
    </>
  );
};

export default Careers;
